import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Crown, RefreshCw } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import {
  getLeaderboard,
  formatPoints,
  type LeaderboardEntry,
  type LeaderboardMediaType,
} from '@/lib/points';
import { cn } from '@/lib/utils';
import UserAvatar from '@/components/UserAvatar';

import './GlobalLeaderboard.css';

interface TabDef {
  key: string;
  label: string;
  media?: LeaderboardMediaType;
}

const TABS: TabDef[] = [
  { key: 'global', label: 'Global' },
  { key: 'audio', label: 'Audio', media: 'audio' },
  { key: 'video', label: 'Video', media: 'video' },
  { key: 'text', label: 'Text', media: 'text' },
  { key: 'image', label: 'Image', media: 'image' },
  { key: 'document', label: 'Doc', media: 'document' },
];

const PodiumSlot: React.FC<{ entry: LeaderboardEntry; highlight: boolean }> = ({
  entry,
  highlight,
}) => {
  const isFirst = entry.rank === 1;
  return (
    <div
      className={cn(
        'corpus-lb-podium__slot',
        `corpus-lb-podium__slot--rank${entry.rank}`,
        isFirst && 'corpus-lb-podium__slot--first',
        highlight && 'corpus-lb-podium__slot--me',
      )}
    >
      <div className="corpus-lb-podium__emblem">
        {isFirst ? (
          <Crown className="corpus-lb-podium__crown" size={22} />
        ) : (
          <span
            className={cn(
              'corpus-lb-podium__medal',
              `corpus-lb-podium__medal--${entry.rank}`,
            )}
          >
            {entry.rank}
          </span>
        )}
      </div>
      <UserAvatar
        name={entry.user_name}
        level={entry.level}
        size={isFirst ? 72 : 56}
      />
      <span className="corpus-lb-podium__name" title={entry.user_name}>
        {entry.user_name}
      </span>
      <span className="corpus-lb-podium__points">
        {formatPoints(entry.total_points)} pts
      </span>
    </div>
  );
};

const Row: React.FC<{ entry: LeaderboardEntry; highlight: boolean }> = ({
  entry,
  highlight,
}) => (
  <div className={cn('corpus-lb-row', highlight && 'corpus-lb-row--me')}>
    <span className="corpus-lb-row__rank">{entry.rank}</span>
    <UserAvatar name={entry.user_name} level={entry.level} size={36} />
    <span className="corpus-lb-row__name" title={entry.user_name}>
      {entry.user_name}
    </span>
    <span className="corpus-lb-row__points">
      {formatPoints(entry.total_points)}
    </span>
  </div>
);

const formatCountdown = (ms: number): string => {
  if (ms <= 0) return 'soon';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

const GlobalLeaderboard: React.FC = () => {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [tab, setTab] = useState<TabDef>(TABS[0]);
  const [now, setNow] = useState(() => Date.now());
  const currentUserId = user?.id;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['points', 'leaderboard', tab.media ?? 'global'],
    queryFn: () => getLeaderboard(token as string, { mediaType: tab.media }),
    enabled: !!token,
  });

  const resetAt = data?.resets_at ? new Date(data.resets_at).getTime() : 0;

  useEffect(() => {
    if (!resetAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [resetAt]);

  const board = data?.leaderboard ?? [];
  const podium = board.slice(0, 3);
  const rows = board.slice(3, 10);
  const ownRank = data?.current_user_rank ?? null;

  const isMe = useMemo(
    () => (entry: LeaderboardEntry) =>
      !!currentUserId && String(entry.user_id) === String(currentUserId),
    [currentUserId],
  );

  // Podium order: 2nd, 1st (centre, elevated), 3rd.
  const podiumOrdered = useMemo(() => {
    const [first, second, third] = podium;
    return [second, first, third].filter(Boolean) as LeaderboardEntry[];
  }, [podium]);

  return (
    <div className="corpus-lb">
      {resetAt > 0 && (
        <div className="corpus-lb__resetrow">
          <span
            className="corpus-lb__reset"
            title={t('common.theMonthlyLeaderboardResetsOnThe1stOfEachMonth')}
          >
            <RefreshCw size={12} className="corpus-lb__reset-icon" />
            {t('common.resetsIn')} {formatCountdown(resetAt - now)}
          </span>
        </div>
      )}

      <div className="corpus-lb__tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === tab.key}
            className={cn(
              'corpus-lb__tab',
              t.key === tab.key && 'corpus-lb__tab--active',
            )}
            onClick={() => setTab(t)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <p className="corpus-lb__msg">{t('messages.loadingLeaderboard')}</p>
      )}
      {isError && (
        <p className="corpus-lb__msg">
          {t('common.couldNotLoadTheLeaderboard')}
        </p>
      )}

      {!isLoading && !isError && board.length === 0 && (
        <p className="corpus-lb__msg">
          {t('common.noContributionsThisMonthYet')}
        </p>
      )}

      {podiumOrdered.length > 0 && (
        <div className="corpus-lb-podium">
          {podiumOrdered.map((entry) => (
            <PodiumSlot
              key={entry.user_id}
              entry={entry}
              highlight={isMe(entry)}
            />
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="corpus-lb__rows">
          {rows.map((entry) => (
            <Row key={entry.user_id} entry={entry} highlight={isMe(entry)} />
          ))}
        </div>
      )}

      {ownRank && (
        <>
          <div className="corpus-lb__divider" />
          <Row entry={ownRank} highlight />
        </>
      )}
    </div>
  );
};

export default GlobalLeaderboard;
